import { gql, TypedDocumentNode, useMutation } from '@apollo/client'
import { ErrorReporting, getErrors } from '@propps/client'
import {
  Button,
  FixedButtonWrapper,
  Input,
  ListItem,
  ListWrapper,
  SectionHeader,
  StackMain,
} from '@propps/ui'
import { useFormik } from 'formik'
import React from 'react'
import { match as Match, useHistory } from 'react-router-dom'
import * as Yup from 'yup'

import {
  CreateDeveloperAppMutation,
  CreateDeveloperAppMutationVariables,
} from './__generated__/CreateDeveloperAppMutation'

const CREATE_DEVELOPER_APP_MUTATION: TypedDocumentNode<
  CreateDeveloperAppMutation,
  CreateDeveloperAppMutationVariables
> = gql`
  mutation CreateDeveloperAppMutation($input: CreateDeveloperAppInput!) {
    result: createDeveloperApp(input: $input) {
      developerApp {
        id
        name
      }
    }
  }
`

export function CreateDeveloperApp({ match }: { match: Match }) {
  const history = useHistory()
  const [mutate] = useMutation(CREATE_DEVELOPER_APP_MUTATION, {
    update: (cache, { data }) => {
      if (data) {
        cache.modify({
          fields: {
            developerApps: (value) => {
              return [
                ...value,
                cache.writeFragment({
                  data: data.result.developerApp,
                  fragment: gql`
                    fragment NewDeveloperApp on DeveloperApp {
                      id
                      name
                    }
                  `,
                }),
              ]
            },
          },
        })
      }
    },
  })

  const formik = useFormik({
    initialValues: { name: '' },
    validationSchema: ValidationSchema,
    onSubmit: async (values, helpers) => {
      try {
        const { data, errors } = await mutate({ variables: { input: values } })

        if (!data) {
          ErrorReporting.report(errors)
          return
        }

        history.push(
          match.url.replace('/create', '/' + data.result.developerApp.id)
        )
      } catch (err) {
        ErrorReporting.report(err)
      }
    },
  })

  return (
    <StackMain variant="app">
      <form onSubmit={formik.handleSubmit}>
        <h1>Create a Developer App</h1>
        <SectionHeader hr h3="Details" />
        <ListWrapper>
          <ListItem>
            <label>Name</label>
            <Input
              type="text"
              {...formik.getFieldProps('name')}
              errors={getErrors(formik, 'name')}
            />
          </ListItem>
        </ListWrapper>
        <FixedButtonWrapper>
          <Button type="submit" cta pending={formik.isSubmitting}>
            Create
          </Button>
        </FixedButtonWrapper>
      </form>
    </StackMain>
  )
}

const ValidationSchema = Yup.object({
  name: Yup.string().required().label('Name'),
})
